/**
 * What the media library accepts, for the three places that have to agree:
 * the label the uploader shows, the filter the browser applies before sending,
 * and the filter multer applies on arrival.

 */
const IMAGE_UPLOAD_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'avif']

/**
 * The values `MediaAsset.format` actually holds, which is what sharp reports
 * for a decoded image.
 *
 * Not the list above: that one exists so a file named `.jpg` and one named
 * `.jpeg` are both pickable, and it is matched against a *filename*. Filtering
 * the library by `jpg` matched no row ever stored, because sharp calls that
 * format `jpeg` and the query compares the column for equality.
 */
export const IMAGE_STORED_FORMATS = ['jpeg', 'png', 'webp', 'avif'] as const

/** The `accept` list, as MIME types the browser matches a picked file against. */
export const IMAGE_UPLOAD_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/avif',
]

/** Multer takes megabytes; the browser-side check takes bytes. */
export const IMAGE_UPLOAD_MAX_MB = 16
export const IMAGE_UPLOAD_MAX_BYTES = IMAGE_UPLOAD_MAX_MB * 1000 * 1000

/** The alternation `StorageService.multerOptions` matches a mimetype against. */
export const IMAGE_UPLOAD_FILE_TYPES = IMAGE_UPLOAD_EXTENSIONS.join('|')

/**
 * How far back the library's period filter looks.
 *
 * A window rather than a pair of dates: the API takes `createdAfter`, and two
 * free date fields let an operator build a range that can only be empty.
 */
export const MEDIA_PERIOD_DAYS = {
  ALL: null,
  DAYS_7: 7,
  DAYS_30: 30,
  DAYS_365: 365,
} as const

export type MediaPeriod = keyof typeof MEDIA_PERIOD_DAYS

/** The orders the media library can be read in. The first is the default. */
export const MEDIA_SORTS = [
  'NEWEST',
  'OLDEST',
  'TITLE',
  'SIZE',
  'USAGE',
] as const

export type MediaSort = (typeof MEDIA_SORTS)[number]

/** The ISO instant a period starts at, or undefined for "all". */
export function periodStart(
  period: MediaPeriod,
  now: Date,
): string | undefined {
  const days = MEDIA_PERIOD_DAYS[period]
  if (days == null) return undefined
  return new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString()
}

/**
 * The ceiling for a direct browser → S3 upload.
 *
 * Higher than the multipart cap because nothing about the request passes
 * through the application: a 60 MB image that would occupy the process for its
 * whole transfer costs one signature here.
 */
export const IMAGE_DIRECT_UPLOAD_MAX_MB = 100
export const IMAGE_DIRECT_UPLOAD_MAX_BYTES =
  IMAGE_DIRECT_UPLOAD_MAX_MB * 1000 * 1000

/** Above this the browser goes straight to S3 rather than through the API. */
export const IMAGE_DIRECT_UPLOAD_THRESHOLD_BYTES = 8 * 1000 * 1000
